import { Task } from '../types';
import { normalizePersonName, isContactVorgang, getContactVorgangDisplayName } from '../components/communication/personModel';
import { hasTaskTimerActivity, normalizeTaskStatus, normalizeTaskType } from './taskStatusAndTypes';

export { normalizePersonName };

export interface TaskFilterCriteria {
  showCompleted?: boolean;
  showArchived?: boolean;
  filterAssignee?: string;
  filterStatus?: string;
  filterType?: string;
  filterPriority?: string;
  searchQuery?: string;
  searchMode?: 'filter' | 'highlight';
  projectName?: string;
  includeDescriptionInSearch?: boolean;
}

/**
 * Checks if a task matches the assignee filter, normalizing academic titles (e.g. "Dr.", "Mag.")
 * and checking both `task.assignee` (comma-separated) and `contactName` on Contact-Vorgänge.
 */
export function matchesAssigneeFilter(task: Task, filterAssignee?: string): boolean {
  if (!filterAssignee || filterAssignee === 'all') return true;
  const normFilterAssignee = normalizePersonName(filterAssignee);
  if (!normFilterAssignee) return true;

  const tAssignee = task.assignee || '';
  if (tAssignee) {
    const assignees = tAssignee.split(',').map(a => normalizePersonName(a.trim()));
    if (assignees.includes(normFilterAssignee)) return true;
  }

  const contactName = task.contactName || (task.type === 'contact' ? task.name : '') || '';
  if (contactName && normalizePersonName(contactName) === normFilterAssignee) {
    return true;
  }

  return false;
}

/**
 * Checks if a task matches the status filter, including the special `'timer'` filter
 * (running timer OR trackedTime > 0) and normalized status comparison.
 */
export function matchesStatusFilter(task: Task, filterStatus?: string, precomputedNormStatus?: string): boolean {
  if (!filterStatus || filterStatus === 'all') return true;
  if (filterStatus === 'timer') {
    return hasTaskTimerActivity(task);
  }
  const currentStatus = task.status || (task.completed ? 'completed' : 'open');
  if (currentStatus === filterStatus) return true;
  const normStatus = precomputedNormStatus ?? normalizeTaskStatus(currentStatus);
  return normStatus === filterStatus;
}

/**
 * Checks if a task matches the priority filter.
 */
export function matchesPriorityFilter(task: Task, filterPriority?: string): boolean {
  if (!filterPriority || filterPriority === 'all') return true;
  return (task.priority || 'none') === filterPriority;
}

/**
 * Checks if a task matches the type filter, supporting both raw type and normalized type.
 */
export function matchesTypeFilter(task: Task, filterType?: string, precomputedNormType?: string): boolean {
  if (!filterType || filterType === 'all') return true;
  if (task.type === filterType) return true;
  const normType = precomputedNormType ?? normalizeTaskType(task.type || 'task');
  return normType === filterType;
}

/**
 * Checks if a task matches the text search query when `searchMode === 'filter'`.
 */
export function matchesSearchFilter(
  task: Task,
  searchQuery?: string,
  searchMode: 'filter' | 'highlight' = 'filter',
  options?: { effectiveName?: string; projectName?: string; includeDescription?: boolean }
): boolean {
  if (searchMode === 'highlight') return true;
  const q = (searchQuery || '').trim().toLowerCase();
  if (!q) return true;

  const effectiveName = options?.effectiveName ?? (getContactVorgangDisplayName(task) || task.name || '');
  if (effectiveName.toLowerCase().includes(q)) return true;
  if ((task.subtitle || '').toLowerCase().includes(q)) return true;
  if ((task.assignee || '').toLowerCase().includes(q)) return true;
  if (options?.includeDescription !== false && (task.description || '').toLowerCase().includes(q)) return true;
  if (options?.projectName && options.projectName.toLowerCase().includes(q)) return true;

  return false;
}

/**
 * Unified predicate for checking whether a task matches the active filter bar criteria.
 */
export function matchesTaskFilter(
  task: Task,
  criteria: TaskFilterCriteria,
  precomputed?: { normStatus?: string; normType?: string; effectiveName?: string }
): boolean {
  const {
    showCompleted = true,
    showArchived = false,
    filterAssignee = 'all',
    filterStatus = 'all',
    filterType = 'all',
    filterPriority = 'all',
    searchQuery = '',
    searchMode = 'filter',
    projectName,
    includeDescriptionInSearch = true,
  } = criteria;

  if (!showArchived && task.archived) return false;

  // K-1: Inline task creation drafts must remain visible while being edited, regardless of active filters
  if (task.isInlineNew && !task.archived) {
    return true;
  }

  const isCompleted = task.completed || task.status === 'completed' || precomputed?.normStatus === 'completed';
  if (!showCompleted && isCompleted) return false;

  if (!matchesAssigneeFilter(task, filterAssignee)) return false;
  if (!matchesStatusFilter(task, filterStatus, precomputed?.normStatus)) return false;
  if (!matchesTypeFilter(task, filterType, precomputed?.normType)) return false;
  if (!matchesPriorityFilter(task, filterPriority)) return false;
  if (
    !matchesSearchFilter(task, searchQuery, searchMode, {
      effectiveName: precomputed?.effectiveName,
      projectName,
      includeDescription: includeDescriptionInSearch,
    })
  ) {
    return false;
  }

  return true;
}
